import { useMemo, useState } from 'react';
import { FileSelectDialog } from '../../../../../../../common/components/v2/FileSelectDialog/FileSelectDialog';
import { FileTypeOption, LoanFile } from '../../constants/loanFile';
import { FileTypeField } from './components/FileTypeField';
import { AddFilesButton } from './components/AddFilesButton';

/** At most this many files can be picked at once, as in the old LoanFilesGrid. */
const MAX_FILES = 5;

interface AddFilesProps {
    id: string;
    fileTypeOptions: FileTypeOption[];
    /** Called with the picked files, as new (unsaved) files of the chosen type. */
    onAdd: (files: LoanFile[]) => void;
}

/** Picks a file type, then files to add as that type. */
export const AddFiles = ({ id, fileTypeOptions, onAdd }: AddFilesProps) => {
    // By name, without Appraisal (added elsewhere), as the old LoanFilesGrid offered them
    const options = useMemo(
        () =>
            fileTypeOptions
                .filter((option) => option.name !== 'Appraisal')
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
        [fileTypeOptions]
    );
    const [chosenFileTypeId, set_chosenFileTypeId] = useState<number | null>(null);
    const [isDialogOpen, set_isDialogOpen] = useState(false);

    // The first type until one is chosen (or if the chosen one is no longer offered)
    const fileType = options.find((option) => option.id === chosenFileTypeId) ?? options[0];

    const addFiles = (selectedFiles: File[]) => {
        set_isDialogOpen(false);
        if (!fileType || !selectedFiles.length) {
            return;
        }
        onAdd(
            selectedFiles.map((file) => ({
                id: 0,
                fileTypeId: fileType.id,
                fileName: file.name,
                fileToUpload: file,
            }))
        );
    };

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <FileTypeField
                    id={`${id}_fileType`}
                    value={fileType?.id ?? null}
                    onChange={set_chosenFileTypeId}
                    options={options}
                />
            </div>
            {/* Lined up with the input (DropDownBox sits 0.5rem below the top of its row) */}
            <div style={{ marginTop: '0.5rem' }}>
                <AddFilesButton
                    id={`${id}_addButton`}
                    onClick={() => set_isDialogOpen(true)}
                    disabled={!fileType}
                />
            </div>
            <FileSelectDialog
                id={`${id}_fileSelect`}
                isOpen={isDialogOpen}
                fileTypeName={fileType?.name}
                maxFiles={MAX_FILES}
                onSubmit={addFiles}
                onCancel={() => set_isDialogOpen(false)}
            />
        </div>
    );
};
